import {useId} from "react";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {useLocale} from "i18n";
import {ContentSurface, Prose} from "components/ContentPage";
import {P2pStage} from "./TransferStages";
import {p2pCopy} from "./copy/p2p";

/**
 * Public, crawlable **Peer-to-peer transfer mode** page (`/p2p`, issue ui#133).
 *
 * Dumb view: all copy comes from {@link ./copy/p2p.ts} (both locales), selected at runtime by
 * `useLocale()`, so the `/ro` SSG tree renders Romanian for free. SSG-safe: pure token-styled markup
 * inside the shared `Prose` typography, no browser APIs, no portals. The component only maps the
 * structured copy (intro → titled sections with bullets → related-mode cross-links) to semantic HTML;
 * all wording (including the honest STUN/no-TURN limitation) lives in the copy module.
 *
 * A content agent fills this page by editing EXACTLY two files: this component (structure) and
 * {@link ./copy/p2p.ts} (strings) — nothing shared.
 */
export function P2PContent() {
	const {locale} = useLocale();
	const copy = p2pCopy[locale];
	return (
		<ContentSurface>
			<Prose>
				<h1>{copy.heading}</h1>
				<P2PBody/>
			</Prose>
		</ContentSurface>
	);
}

/** Route-neutral P2P copy shared by the in-flow page and the Resources dialog. */
export function P2PBody() {
	const {locale} = useLocale();
	const copy = p2pCopy[locale];
	const idPrefix = useId();
	return (
		<>
				<p>{copy.lede}</p>
				{copy.intro.map((paragraph, index) => (
					<p key={index}>{paragraph}</p>
				))}

				{/* Decorative anchor: the same animated P2P stage the features dialog shows (ui#136) — the
				    packet hopping the dashed peer-to-peer wire. Constrained + centred so it reads as a hero
				    above the crawlable copy. */}
				<div style={{maxWidth: 460, margin: "6px auto 26px"}}>
					<P2pStage/>
				</div>

				{copy.sections.map((section, sectionIndex) => {
					const headingId = `${idPrefix}-section-${sectionIndex}`;
					return (
						<section key={headingId} aria-labelledby={headingId}>
							<h2 id={headingId}>{section.title}</h2>
							{section.paragraphs?.map((paragraph, index) => (
								<p key={index}>{paragraph}</p>
							))}
							{section.bullets && (
								<ul>
									{section.bullets.map((bullet, index) => (
										<li key={index}>
											{bullet.term && <strong>{bullet.term}</strong>}
											{bullet.term && " "}
											{bullet.text}
										</li>
									))}
								</ul>
							)}
						</section>
					);
				})}

				<section aria-labelledby={`${idPrefix}-related`}>
					<h2 id={`${idPrefix}-related`}>{copy.related.title}</h2>
					<p>{copy.related.intro}</p>
					<ul>
						{copy.related.items.map((item) => (
							<li key={item.to}>
								<RouterLink to={item.to}>{item.label}</RouterLink> - {item.note}
							</li>
						))}
					</ul>
				</section>
		</>
	);
}
